import { useCallback, useEffect, useRef, useState } from 'react';

// Import constants
import {
  INITIAL_END_HOURS,
  INITIAL_START_HOURS,
} from '../../constants/schedule';

// Import store
import { useScheduleStore } from '../../store/scheduleStore';

// Import hooks
import useLocalTimezone from '../timezone/useLocalTimezone';

// Import types
import type { DateRange } from '../../types/date';

interface SnapshotRef {
  eventTitle: string;
  eventTimezone: string;
  pickerStart: string;
  pickerEnd: string;
  pickerRange: DateRange;
}

export function useEventModal(
  initialStart = INITIAL_START_HOURS,
  initialEnd = INITIAL_END_HOURS,
  initialRange: DateRange = { from: undefined, to: undefined },
) {
  const localTimezone = useLocalTimezone();

  // Modal state - UI component state, not persisted
  const [showPopup, setShowPopup] = useState(false);

  // Event title and timezone persisted in Zustand
  const eventTitle = useScheduleStore((s) => s.eventTitle);
  const setEventTitle = useScheduleStore((s) => s.setEventTitle);
  const eventTimezone = useScheduleStore((s) => s.eventTimezone);
  const setEventTimezone = useScheduleStore((s) => s.setEventTimezone);

  // Picker state - can still use Zustand if needed for persistence, or useState
  const pickerStart = useScheduleStore((s) => s.pickerStart);
  const pickerEnd = useScheduleStore((s) => s.pickerEnd);
  const pickerRange = useScheduleStore((s) => s.pickerRange);
  const setPickerStart = useScheduleStore((s) => s.setPickerStart);
  const setPickerEnd = useScheduleStore((s) => s.setPickerEnd);
  const setPickerRange = useScheduleStore((s) => s.setPickerRange);

  const snapshotRef = useRef<SnapshotRef | null>(null);

  // set defaults if untouched
  useEffect(() => {
    if (pickerStart === undefined) setPickerStart(initialStart);
    if (pickerEnd === undefined) setPickerEnd(initialEnd);
    if (!pickerRange) setPickerRange(initialRange);
  }, [
    initialStart,
    initialEnd,
    initialRange,
    pickerStart,
    pickerEnd,
    pickerRange,
    setPickerStart,
    setPickerEnd,
    setPickerRange,
  ]);

  // Initialize timezone
  useEffect(() => {
    const isEmptyTimezone = !eventTimezone || eventTimezone === '';
    if (localTimezone && isEmptyTimezone) {
      setEventTimezone(localTimezone);
    }
  }, [localTimezone, eventTimezone, setEventTimezone]);

  const openPopup = useCallback(() => {
    snapshotRef.current = {
      eventTitle,
      eventTimezone,
      pickerStart,
      pickerEnd,
      pickerRange,
    };
    setShowPopup(true);
  }, [pickerEnd, pickerRange, pickerStart, eventTimezone, eventTitle]);

  const cancelPopup = useCallback(() => {
    const snap = snapshotRef.current;
    if (snap) {
      setEventTitle(snap.eventTitle);
      setEventTimezone(snap.eventTimezone);
      setPickerStart(snap.pickerStart);
      setPickerEnd(snap.pickerEnd);
      setPickerRange(snap.pickerRange);
    } else {
      // Fallback: reset to passed-in defaults
      setEventTitle('');
      setEventTimezone(localTimezone);
      setPickerStart(initialStart);
      setPickerEnd(initialEnd);
      setPickerRange(initialRange);
    }
    setShowPopup(false);
  }, [
    initialEnd,
    initialRange,
    initialStart,
    localTimezone,
    setPickerEnd,
    setPickerRange,
    setPickerStart,
    setEventTimezone,
    setEventTitle,
  ]);

  const closePopup = useCallback(() => setShowPopup(false), []);

  return {
    showPopup,
    openPopup,
    cancelPopup,
    closePopup,
    eventTitle,
    setEventTitle,
    eventTimezone,
    setEventTimezone,
    pickerRange,
    setPickerRange,
    pickerStart,
    setPickerStart,
    pickerEnd,
    setPickerEnd,
  };
}

export default useEventModal;
